import { useCallback } from 'react';
import { useGetAccountFoldersQuery, accountFoldersApi } from 'src/api/redux';
import { useAppDispatch } from 'src/redux/hooks';
import type { AccountFoldersResponse } from 'src/api/redux/accountFolders';

/**
 * Hook to fetch account folders using RTK Query
 */
const useGetAccountFolders = () => {
  const dispatch = useAppDispatch();
  const { data, error, isLoading, refetch } = useGetAccountFoldersQuery();

  /**
   * Mutate function for cache updates, memoized so callers can list it as a
   * dependency.
   * - When called with no arguments, triggers a refetch
   * - When called with an updater function, performs optimistic update
   */
  const mutate = useCallback(
    (
      updater?: (
        current: AccountFoldersResponse | undefined
      ) => AccountFoldersResponse | undefined,
      shouldRevalidate = true
    ) => {
      if (updater) {
        // Optimistic update using RTK Query's updateQueryData
        dispatch(
          accountFoldersApi.util.updateQueryData(
            'getAccountFolders',
            undefined,
            (draft) => {
              const updated = updater(draft as AccountFoldersResponse);
              if (updated) {
                Object.assign(draft, updated);
              }
            }
          )
        );
      }

      if (shouldRevalidate) {
        refetch();
      }
    },
    [dispatch, refetch]
  );

  return {
    data,
    error,
    isLoading,
    mutate,
    refetch,
  };
};

export default useGetAccountFolders;
